import { hasAvailability } from '@utils/helpers';

import { useGetSpaTreatments, useGetSpaTreatmentsPage } from '@features/spa/hooks';

import { SpaListItem } from 'voyager';
import { SpaEntityType } from 'shared-types/Spa';

type UseSpaTreatmentsListParams = {
  propertyId: number;
  language: string;
  categoryFilterId: number | null;
  date: string;
};

const mapToListItem = (entity: SpaEntityType, fallbackImageUrl?: string): SpaListItem => ({
  id: String(entity.id),
  name: entity.name,
  imageUrl: entity.image?.url || fallbackImageUrl,
  price:
    entity.price?.amount != null
      ? { amount: entity.price.amount, currencyCode: entity.price.currencyCode }
      : undefined,
  duration: entity.displayDuration > 0 ? `${entity.displayDuration} min` : undefined,
  numberOfGuests: entity.numberOfGuests,
  unavailable: !entity.availability,
});

export function useSpaTreatmentsList({
  propertyId,
  language,
  categoryFilterId,
  date,
}: UseSpaTreatmentsListParams) {
  const { spaTreatments = [], isFetching: isFetchingTreatments } = useGetSpaTreatments(
    propertyId,
    language,
    date,
    categoryFilterId
  );

  const { data: treatmentsPage } = useGetSpaTreatmentsPage(propertyId, language);
  const fallbackImageUrl = treatmentsPage?.heroBackgroundImage?.url;

  const sortedTreatments = spaTreatments.slice().sort((a, b) => a.priority - b.priority);

  const availableTreatments = sortedTreatments.filter((item) => hasAvailability(item, date));

  const unavailableTreatments = sortedTreatments.filter((item) => !hasAvailability(item, date));

  const allEntities = [...availableTreatments, ...unavailableTreatments];

  const filteredEntities = categoryFilterId
    ? allEntities.filter(({ categories }) =>
        categories.find(
          (category: { id: string | number }) => String(category.id) === String(categoryFilterId)
        )
      )
    : allEntities;

  const listItems: SpaListItem[] = filteredEntities.map((entity) =>
    mapToListItem(entity, fallbackImageUrl)
  );

  return {
    listItems,
    isFetching: isFetchingTreatments,
  };
}
